import React, {type ReactElement} from "react";
import axios from "axios";
import {css} from "/src/utils/utils";

import {logEvent} from "../../utils/amplitude";

import ModuleBody from "../layouts/module-body/ModuleBody";
import ShipmentEntryInputGroup from "./ShipmentEntryInputGroup";
import type {ShipmentEntryInputData} from "./ShipmentEntryInputGroup";
import CalculatedShipmentRow from "./CalculatedShipmentRow";
import Button from "../buttons/button/Button";
import CTAButton from "../buttons/cta-button/CTAButton";

import useIncrementalKey from "./useIncrementalKey";

import * as styles from "./styles/open-emissions-calculator.module.scss";

import {
  ActionEnum,
  EmissionsCalculatorReducer,
  InitialEmissionsCalculatorState,
  initialFieldErrors,
  STATUS_ENUM,
} from "./EmissionsCalculatorReducer";
import {ShipmentDetail} from "./EmissionsCalculatorReducer";

import {validateAllFields} from "./utils/validate";
import {buildRequestObject} from "./utils/utils";

const MAX_ALLOWED_SHIPMENT_LEGS = 5;

const OpenEmissionsCalculator = (): ReactElement => {
  const [showResults, setShowResults] = React.useState<boolean>(false);

  const [latestLeg, setLatestLeg] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [highlightedLeg, setHighlightedLeg] = React.useState<
    number | undefined
  >(undefined);

  const {getNext} = useIncrementalKey(0);

  const [state, dispatch] = React.useReducer(
    EmissionsCalculatorReducer,
    InitialEmissionsCalculatorState
  );

  const handleAddShipmentEntry = React.useCallback(() => {
    const id = getNext();
    dispatch({
      type: ActionEnum.AddShipmentDetail,
      payload: {id, status: STATUS_ENUM.NEW, fieldErrors: initialFieldErrors},
    });
  }, [getNext]);

  const handleRemoveShipmentEntry = React.useCallback((id: string) => {
    dispatch({type: ActionEnum.RemoveShipmentDetail, payload: {id}});
  }, []);

  const handleCalculateEmissions = React.useCallback(() => {
    logEvent("click:emissions_calculator_calculate_button");

    if (Object.keys(state).length >= MAX_ALLOWED_SHIPMENT_LEGS) {
      logEvent("load:emissions_calculator_maximum_shipments_calculated");
    }

    const shipmentDetail = state[latestLeg];
    const requestBody = buildRequestObject(shipmentDetail.data);
    const {errors, isValid} = validateAllFields(requestBody);
    if (!shipmentDetail.calculatedEmissions) {
      dispatch({
        type: ActionEnum.UpdateShipmentStatus,
        payload: {
          id: shipmentDetail.id,
          status: STATUS_ENUM.CALCULATING,
        },
      });
    }
    if (isValid) {
      setIsLoading(true);
      axios
        .post("/.netlify/functions/carbon-calculation-api", requestBody)
        .then(response => {
          const carbonData = response.data.data;
          const emissions = carbonData.co2e_emissions.value;
          handleAddShipmentEntry();
          setLatestLeg(latestLeg => latestLeg + 1);
          dispatch({
            type: ActionEnum.UpdateShipmentDetail,
            payload: {
              id: shipmentDetail.id,
              calculatedEmissions: emissions,
              data: shipmentDetail.data,
              status: STATUS_ENUM.SUCCESS,
              fieldErrors: initialFieldErrors,
            },
          });
        })
        .catch(error => {
          const errorMessage = error.response.data.error.message;
          dispatch({
            type: ActionEnum.UpdateShipmentDetail,
            payload: {
              id: shipmentDetail.id,
              calculatedEmissions: undefined,
              data: shipmentDetail.data,
              status: STATUS_ENUM.API_ERROR,
              apiError: errorMessage,
              fieldErrors: initialFieldErrors,
            },
          });
        })
        .finally(() => {
          setIsLoading(false);
          setHighlightedLeg(1);
          setTimeout(() => {
            setHighlightedLeg(undefined);
          }, 3000);
        });
    } else {
      setIsLoading(false);
      dispatch({
        type: ActionEnum.UpdateShipmentDetail,
        payload: {
          id: shipmentDetail.id,
          calculatedEmissions: undefined,
          data: shipmentDetail.data,
          status: STATUS_ENUM.ERROR,
          fieldErrors: errors,
        },
      });
    }
  }, [state, handleAddShipmentEntry, latestLeg]);

  const handleShipmentDetailsChange = React.useCallback(
    (entry: ShipmentDetail) => (data: ShipmentEntryInputData) => {
      dispatch({
        type: ActionEnum.UpdateShipmentDetail,
        payload: {
          id: entry.id,
          data,
          status: entry.status,
          fieldErrors: entry.fieldErrors,
        },
      });
    },
    []
  );

  const totalEmissions = React.useMemo(() => {
    if (Object.keys(state).length === 0) {
      setShowResults(false); // Reset showResults when there are no entries
      return undefined;
    }

    let total = 0;
    Object.keys(state).forEach(id => {
      const shipmentDetail = state[id];
      const emissions = shipmentDetail.calculatedEmissions ?? 0;
      total += emissions;
    });

    if (total > 0) {
      setShowResults(true);
    }

    return total.toFixed(2);
  }, [state]);

  const disableCalculation = React.useMemo((): boolean => {
    if (Object.keys(state).length > MAX_ALLOWED_SHIPMENT_LEGS) {
      return true;
    }
    return false;
  }, [state]);

  return (
    <section className={css(styles.openEmissionsCalculator)}>
      <ModuleBody>
        {disableCalculation ? (
          <div className={styles.openEmissionsCalculator__signupDiv}>
            <p>
              🎉 Great work! You&#39;ve calculated {MAX_ALLOWED_SHIPMENT_LEGS}{" "}
              shipments emissions! Sign up for Flexport to calculate more!
            </p>
            <CTAButton
              cta={{
                label: "Sign up",
                url: "/open-emissions-signup",
              }}
              small={true}
              classes={styles.openEmissionsCalculator__calculateButton}
            />
          </div>
        ) : (
          <div>
            <div>
              <h2 className={styles.openEmissionsCalculator__titleText}>
                Calculate Emissions
              </h2>
              <h2 className={styles.openEmissionsCalculator__headerText}>
                Add shipment leg one by one to calculate emissions.
              </h2>
              <br />
            </div>
            <div
              className={styles.openEmissionsCalculator__formRow}
              key={latestLeg}
            >
              <ShipmentEntryInputGroup
                data={state[latestLeg].data}
                fieldErrors={state[latestLeg].fieldErrors}
                onChange={handleShipmentDetailsChange(state[latestLeg])}
              />
              <div className={styles.openEmissionsCalculator__buttonDiv}>
                <Button
                  type="button"
                  kind="primary"
                  onClick={handleCalculateEmissions}
                  classes={styles.openEmissionsCalculator__calculateButton}
                  small={true}
                  loading={isLoading}
                  disabled={disableCalculation}
                >
                  Calculate
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Choosing not to optimize the below piece of code since we're only ever going to have at most 10 objects in the state and the performance difference is negligible */}
        {Object.keys(state)
          .reverse()
          .map((key, index) => {
            return (
              <div
                key={state[key].id}
                className={styles.openEmissionsCalculator__entryGroup}
              >
                {state[key].calculatedEmissions && (
                  <div>
                    <CalculatedShipmentRow
                      shipmentDetail={state[key]}
                      highlight={index === highlightedLeg}
                      hideCloseButton={disableCalculation}
                      onClose={handleRemoveShipmentEntry}
                    />
                  </div>
                )}
              </div>
            );
          })}

        <div className={styles.openEmissionsCalculator__totalEmissionsRow}>
          {showResults && (
            <div className={styles.openEmissionsCalculator__results}>
              <p>Total emissions &nbsp;</p>
              <p className={styles.openEmissionsCalculator__resultsText}>
                {totalEmissions} TCO<sub>2</sub>e
              </p>
            </div>
          )}
        </div>
        <br />
      </ModuleBody>
    </section>
  );
};

export default OpenEmissionsCalculator;
